import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { SkipLink } from '../components/SkipLink';
import { toast } from 'sonner';
import { Send, CheckCircle2, MessageSquare, PlayCircle, Brain, ShieldCheck, Mic, Volume2, AlertCircle, Heart } from 'lucide-react';
import { VoiceMode } from '../components/VoiceMode';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const PublicFormFill = ({ isSlug = false }) => {
  const params = useParams();
  const code = isSlug ? params.slug : params.code;
  const [qrInfo, setQrInfo] = useState(null);
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [lastMessage, setLastMessage] = useState('');
  const [resolvedCode, setResolvedCode] = useState(null);
  const [interviewMode, setInterviewMode] = useState('strict');
  const [resumeInfo, setResumeInfo] = useState(null);
  const [submissionResult, setSubmissionResult] = useState(null);
  const [gdprConsent, setGdprConsent] = useState(false);
  const [showConsentGate, setShowConsentGate] = useState(false);
  const [startError, setStartError] = useState('');
  const [autoStartVoice, setAutoStartVoice] = useState(false);
  const [voicePhase, setVoicePhase] = useState('idle');
  const [progress, setProgress] = useState({ answered: 0, total: 0, step: 1 });
  const [consentAudioPlaying, setConsentAudioPlaying] = useState(false);
  const [consentAudioPlayed, setConsentAudioPlayed] = useState(false);
  const [listeningConsent, setListeningConsent] = useState(false);
  const sharedAudioRef = useRef(null);
  const consentRetriesRef = useRef(0);
  const listenForConsentRef = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Single audio element unlocked within the user's first tap so EVERY later
  // TTS (consent, questions, clarifications) plays reliably on iOS/Android —
  // the same element is reused by VoiceMode (via sharedAudioRef) so no autoplay
  // block ever occurs after the gesture.
  const SILENT_WAV = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAgD4AAAB9AAACABAAZGF0YQAAAAA=';
  const ensureAudioUnlocked = useCallback(() => {
    try {
      if (!sharedAudioRef.current) {
        sharedAudioRef.current = new Audio();
        sharedAudioRef.current.preload = 'auto';
      }
      const el = sharedAudioRef.current;
      el.src = SILENT_WAV;
      el.muted = true;
      const p = el.play();
      if (p && p.then) {
        p.then(() => { el.pause(); el.currentTime = 0; el.muted = false; })
         .catch(() => { el.muted = false; });
      }
    } catch (e) { /* noop */ }
  }, []);

  // Derived branding (white-label) + welcome text
  const branding = qrInfo?.branding || {};
  const brandColor = branding.primary_color || '#1d4ed8';
  const companyName = branding.brand_name || '';
  const brandLogo = branding.logo_url || '';
  const welcomeText = qrInfo?.welcome_message || branding.welcome_text || '';

  // Latest assistant message (used to voice the first question on hands-free auto-start)
  const latestAssistant = React.useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'assistant') return messages[i].content;
    }
    return '';
  }, [messages]);

  useEffect(() => {
    fetchQRInfo();
  }, [code]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (messages.length > 0) {
      const latestMessage = messages[messages.length - 1];
      if (latestMessage.role === 'assistant') {
        setLastMessage(latestMessage.content);
      }
    }
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchQRInfo = async () => {
    try {
      const endpoint = isSlug ? `${API}/public/f/${code}` : `${API}/public/qr/${code}`;
      const response = await axios.get(endpoint);
      setQrInfo(response.data);
      setInterviewMode(response.data.interview_mode || 'strict');
      if (isSlug && response.data.unique_code) {
        setResolvedCode(response.data.unique_code);
      }

      // Check for existing paused session in localStorage
      const savedSessionId = localStorage.getItem(`er_session_${code}`);
      if (savedSessionId) {
        try {
          const resumeRes = await axios.get(`${API}/public/sessions/${savedSessionId}/resume-info`);
          // Resume both explicitly paused sessions AND sessions left mid-way by a
          // disconnect/refresh (still "in_progress"), so no conversation is ever
          // orphaned. Never resume completed ones.
          const st = resumeRes.data.status;
          if (st === 'paused' || st === 'in_progress') {
            setResumeInfo(resumeRes.data);
            setLoading(false);
            return; // Show resume UI, don't start new session
          }
          if (st === 'completed') {
            localStorage.removeItem(`er_session_${code}`);
          }
        } catch {
          localStorage.removeItem(`er_session_${code}`);
        }
      }

      // Show GDPR consent gate before starting session
      setShowConsentGate(true);
    } catch (error) {
      toast.error('Invalid or expired QR code');
    } finally {
      setLoading(false);
    }
  };

  const handleResume = async () => {
    if (!resumeInfo) return;
    ensureAudioUnlocked();
    setResumeInfo(null);
    setSubmissionResult(null);
    setLoading(true);
    try {
      // Load existing messages
      const messagesRes = await axios.get(`${API}/public/sessions/${resumeInfo.session_id}/messages`);
      setSession(messagesRes.data.session);
      const existingMsgs = (messagesRes.data.messages || []).map(m => ({
        id: m.id,
        role: m.role,
        content: m.content,
        timestamp: m.timestamp,
      }));
      setMessages(existingMsgs);

      // Send resume message to continue
      const summary = resumeInfo.captured_summary
        .map(c => `${c.question}: ${c.answer}`)
        .join(', ');
      await sendMessage(
        `Επέστρεψα. Ορίστε τι έχω δώσει μέχρι τώρα: ${summary}. Ας συνεχίσουμε.`,
        true,
        resumeInfo.session_id
      );
      setAutoStartVoice(true);
    } catch {
      toast.error('Failed to resume. Starting fresh.');
      await startSession();
    } finally {
      setLoading(false);
    }
  };

  const handleStartFresh = async () => {
    ensureAudioUnlocked();
    localStorage.removeItem(`er_session_${code}`);
    setResumeInfo(null);
    await startSession();
  };

  const CONSENT_TEXT_EL = 'Καλώς ήρθατε. Πριν ξεκινήσουμε, σας ενημερώνουμε ότι με τη συνέχεια συναινείτε στη συλλογή και επεξεργασία των δεδομένων που θα δώσετε σε αυτή τη συνεδρία. Οι απαντήσεις σας αποθηκεύονται με ασφάλεια και κοινοποιούνται μόνο στον οργανισμό που δημιούργησε τη φόρμα. Μπορείτε να ζητήσετε τη διαγραφή των δεδομένων σας οποιαδήποτε στιγμή. Αν συμφωνείτε, πείτε «Συναινώ» για να ξεκινήσουμε.';

  // Play the privacy/consent disclaimer aloud (Greek TTS). Resolves when playback ends.
  const playConsentAudio = useCallback(() => new Promise((resolve) => {
    (async () => {
      // Guard against concurrent invocations (e.g. Listen then Start)
      if (consentAudioPlaying) { resolve(); return; }
      try {
        setConsentAudioPlaying(true);
        const voicePref = qrInfo?.voice_preference || 'nova';
        const res = await axios.post(`${API}/voice/synthesize`,
          { text: CONSENT_TEXT_EL, voice: voicePref, language: 'el' },
          { responseType: 'blob', timeout: 20000 }
        );
        const url = URL.createObjectURL(res.data);
        const audio = sharedAudioRef.current || new Audio();
        sharedAudioRef.current = audio;
        audio.src = url;
        const done = () => {
          URL.revokeObjectURL(url);
          setConsentAudioPlaying(false);
          setConsentAudioPlayed(true);
          resolve();
        };
        audio.onended = done;
        audio.onerror = done;
        await audio.play().catch(() => done());
      } catch (e) {
        setConsentAudioPlaying(false);
        setConsentAudioPlayed(true); // don't trap the user if TTS fails
        resolve();
      }
    })();
  }), [qrInfo, CONSENT_TEXT_EL, consentAudioPlaying]);

  // Speak an arbitrary Greek prompt via TTS. Resolves when playback ends.
  const playTts = useCallback((text) => new Promise((resolve) => {
    (async () => {
      try {
        const voicePref = qrInfo?.voice_preference || 'nova';
        const res = await axios.post(`${API}/voice/synthesize`,
          { text, voice: voicePref, language: 'el' },
          { responseType: 'blob', timeout: 20000 }
        );
        const url = URL.createObjectURL(res.data);
        const audio = sharedAudioRef.current || new Audio();
        sharedAudioRef.current = audio;
        audio.src = url;
        const done = () => { URL.revokeObjectURL(url); resolve(); };
        audio.onended = done;
        audio.onerror = done;
        await audio.play().catch(() => done());
      } catch (e) { resolve(); }
    })();
  }), [qrInfo]);

  const handleConsentAndStart = async (forceConsent = false) => {
    if (!gdprConsent && !forceConsent) {
      toast.error('Παρακαλώ αποδεχτείτε τους όρους επεξεργασίας δεδομένων για να συνεχίσετε.');
      return;
    }
    ensureAudioUnlocked();
    setShowConsentGate(false);
    setListeningConsent(false);
    setLoading(true);
    await startSession();
    setLoading(false);
  };

  // Spoken + written guidance when consent speech is unclear/noisy, then an
  // automatic re-listen (bounded) so the flow never locks and never needs a reload.
  const CONSENT_GUIDANCE_EL = 'Παρακαλώ πείτε καθαρά «Συναινώ» ή πατήστε το κουτάκι συναίνεσης στην οθόνη για να συνεχίσετε γραπτά.';

  const handleConsentUnclear = useCallback(async () => {
    setListeningConsent(false);
    toast('Δεν σας άκουσα καθαρά. Πείτε «Συναινώ» ή χρησιμοποιήστε το κουτάκι συναίνεσης.');
    await playTts(CONSENT_GUIDANCE_EL);
    if (consentRetriesRef.current < 2 && listenForConsentRef.current) {
      consentRetriesRef.current += 1;
      await listenForConsentRef.current();
    }
  }, [playTts, CONSENT_GUIDANCE_EL]);

  // Hands-free voice consent: listen for an affirmative Greek phrase and proceed
  // automatically. Flexible matching (unaccented, lowercased) — not a fragile
  // exact-string check. The user can always retry with voice without reloading.
  const listenForConsent = useCallback(async () => {
    try {
      setListeningConsent(true);
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
      const chunks = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        try {
          const blob = new Blob(chunks, { type: 'audio/webm' });
          const fd = new FormData();
          fd.append('file', blob, 'consent.webm');
          fd.append('language', 'el');
          const r = await axios.post(`${API}/voice/transcribe`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' }, timeout: 20000,
          });
          const raw = (r.data?.text || r.data?.transcript || '');
          // Normalize: lowercase + strip Greek accents for flexible matching.
          const norm = raw.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
          const yes = norm && /(^|\s)(συναινω|συμφωνω|ναι|αποδεχομαι|μαλιστα|ενταξει)(\s|$)|συναιν|συμφων|αποδεχ|ξεκιν/.test(norm);
          if (yes) {
            setGdprConsent(true);
            await handleConsentAndStart(true);
          } else {
            await handleConsentUnclear();
          }
        } catch (err) {
          await handleConsentUnclear();
        }
      };
      rec.start();
      setTimeout(() => { if (rec.state === 'recording') rec.stop(); }, 6000);
    } catch (e) {
      setListeningConsent(false);
      toast('Δεν έχουμε πρόσβαση στο μικρόφωνο. Επιλέξτε το κουτάκι συναίνεσης για να συνεχίσετε.');
    }
  }, [handleConsentAndStart, handleConsentUnclear]);

  useEffect(() => { listenForConsentRef.current = listenForConsent; }, [listenForConsent]);

  // The single tap that unlocks the voice experience: speak the consent aloud,
  // then start listening for the spoken «Συναινώ».
  const startVoiceConsent = useCallback(async () => {
    ensureAudioUnlocked();
    consentRetriesRef.current = 0;
    await playConsentAudio();
    await listenForConsent();
  }, [playConsentAudio, listenForConsent, ensureAudioUnlocked]);

  const startSession = async () => {
    setSubmissionResult(null);
    try {
      const endpoint = isSlug ? `${API}/public/f/${code}/start` : `${API}/public/qr/${code}/start`;
      const response = await axios.post(endpoint, { gdpr_consent: true });
      setSession(response.data);
      localStorage.setItem(`er_session_${code}`, response.data.id);
      if (response.data.total_questions) {
        setProgress(prev => ({ ...prev, total: response.data.total_questions }));
      }
      
      // Send initial greeting (branded welcome is shown separately in the UI).
      // Pass the fresh session id — React state (session) isn't updated yet.
      await sendMessage("Γεια! Είμαι έτοιμος να ξεκινήσω.", true, response.data.id);
      // Hands-free: auto-start voice mode now that the first question is available
      setAutoStartVoice(true);
    } catch (error) {
      console.error('Failed to start session:', error);
      const detail = error?.response?.data?.detail;
      toast.error(detail || 'Could not load this form. Please try again.');
      setStartError(detail || 'Could not load this form.');
    }
  };

  const sendMessage = async (messageContent, isInitial = false, sessionIdOverride = null) => {
    const sid = sessionIdOverride || session?.id;
    if (!sid) return;

    try {
      const response = await axios.post(
        `${API}/public/sessions/${sid}/chat`,
        { content: messageContent }
      );
      
      await fetchMessages(sid);

      // Update progress indicator (Ερώτηση X από Y)
      const d = response.data;
      setProgress(prev => ({
        answered: d.answered_count ?? prev.answered,
        total: d.total_questions ?? prev.total,
        step: d.current_step ?? prev.step,
      }));

      if (response.data.session_status === 'completed') {
        setSession(prev => ({ ...prev, status: 'completed' }));
        // Surface the real Google/MS/Typeform submission outcome to the responder
        if (response.data.submission) {
          setSubmissionResult(response.data.submission);
        }
        // The interview is over — clear the resume marker so a fresh scan starts anew
        localStorage.removeItem(`er_session_${code}`);
        if (!isInitial) {
          if (response.data.submission && response.data.submission.submitted === false) {
            toast.error('Οι απαντήσεις αποθηκεύτηκαν, αλλά δεν στάλθηκαν στη φόρμα.');
          } else {
            toast.success('Η φόρμα ολοκληρώθηκε και στάλθηκε!');
          }
        }
      }
      if (response.data.session_status === 'paused') {
        setSession(prev => ({ ...prev, status: 'paused' }));
      }
    } catch (error) {
      if (!isInitial) {
        toast.error('Failed to send message');
      }
    }
  };

  const fetchMessages = async (sessionIdOverride = null) => {
    const sid = sessionIdOverride || session?.id;
    if (!sid) return;

    try {
      const response = await axios.get(`${API}/public/sessions/${sid}/messages`);
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || sending) return;

    const userMessage = input.trim();
    setInput('');
    setSending(true);

    await sendMessage(userMessage);
    
    setSending(false);
    inputRef.current?.focus();
  };

  // GDPR Consent Gate
  if (showConsentGate && qrInfo) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <SkipLink />
        <div className="max-w-md w-full" data-testid="gdpr-consent-gate">
          <div className="text-center mb-6">
            {brandLogo ? (
              <img
                src={brandLogo}
                alt={companyName || 'Logo'}
                className="h-14 mx-auto mb-3 object-contain"
                onError={(e) => { e.target.style.display = 'none'; }}
                data-testid="brand-logo-gdpr"
              />
            ) : (
              <ShieldCheck className="h-12 w-12 mx-auto mb-3" style={{ color: brandColor }} />
            )}
            {companyName && (
              <p className="text-sm font-bold mb-1" style={{ color: brandColor }} data-testid="brand-name-gdpr">
                {companyName}
              </p>
            )}
            <h1 className="text-2xl font-black mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {qrInfo.title}
            </h1>
            {qrInfo.description && (
              <p className="text-sm text-[#475569] mt-1">{qrInfo.description}</p>
            )}
          </div>

          <div className="bg-[#F8FAFC] rounded-xl p-5 mb-5 border border-[#E2E8F0]">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold">Ειδοποίηση Επεξεργασίας Δεδομένων</h2>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={playConsentAudio}
                disabled={consentAudioPlaying}
                className="rounded-full h-8 px-3 text-xs"
                data-testid="consent-listen-btn"
                aria-label="Ακούστε τη δήλωση συναίνεσης"
              >
                {consentAudioPlaying ? (
                  <><Volume2 className="h-3.5 w-3.5 mr-1 animate-pulse" /> Αναπαραγωγή…</>
                ) : (
                  <><Volume2 className="h-3.5 w-3.5 mr-1" /> Ακούστε</>
                )}
              </Button>
            </div>
            <p className="text-xs text-[#475569] leading-relaxed mb-4">
              Με τη συνέχεια συναινείτε στη συλλογή και επεξεργασία των δεδομένων που θα δώσετε
              σε αυτή τη συνεδρία. Οι απαντήσεις σας αποθηκεύονται με ασφάλεια και κοινοποιούνται
              μόνο στον οργανισμό που δημιούργησε τη φόρμα. Μπορείτε να ζητήσετε τη διαγραφή των
              δεδομένων σας οποιαδήποτε στιγμή επικοινωνώντας με τον κάτοχο της φόρμας.
            </p>

            <Button
              onClick={startVoiceConsent}
              disabled={consentAudioPlaying || listeningConsent}
              className="w-full rounded-full text-white h-12 text-base disabled:opacity-60"
              style={{ backgroundColor: brandColor }}
              data-testid="voice-consent-start-btn"
            >
              {consentAudioPlaying ? (
                <><Volume2 className="h-4 w-4 mr-2 animate-pulse" /> Ανάγνωση δήλωσης…</>
              ) : listeningConsent ? (
                <><Mic className="h-4 w-4 mr-2 animate-pulse" /> Ακούω… πείτε «Συναινώ»</>
              ) : (
                <><Mic className="h-4 w-4 mr-2" /> Ξεκινήστε με φωνή</>
              )}
            </Button>
            <p className="text-xs text-center text-[#475569] mt-2">
              Πατήστε μία φορά, ακούστε τη δήλωση και πείτε «<strong>Συναινώ</strong>» για να ξεκινήσουμε.
            </p>

            <div className="mt-5 pt-4 border-t border-[#E2E8F0]">
              <label
                className="flex items-start gap-3 cursor-pointer select-none group"
                data-testid="gdpr-consent-label"
              >
                <input
                  type="checkbox"
                  checked={gdprConsent}
                  onChange={(e) => setGdprConsent(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-[#CBD5E1] text-[#1d4ed8] focus:ring-[#1d4ed8]"
                  data-testid="gdpr-consent-checkbox"
                />
                <span className="text-xs text-[#334155] leading-relaxed">
                  Ή, χωρίς φωνή: συναινώ στην επεξεργασία των δεδομένων μου (GDPR) και θα συμπληρώσω γραπτά.
                </span>
              </label>
              <Button
                onClick={() => handleConsentAndStart(false)}
                disabled={!gdprConsent || consentAudioPlaying}
                variant="outline"
                className="w-full rounded-full mt-3 disabled:opacity-50"
                data-testid="gdpr-consent-start-btn"
              >
                Συνέχεια γραπτά
              </Button>
            </div>
          </div>

          <p className="text-center text-[10px] text-[#94A3B8] mt-4">
            Powered by <Link to="/" className="text-[#1d4ed8] hover:underline">Evenly Respond</Link>
          </p>
        </div>
      </div>
    );
  }

  // Resume UI
  if (resumeInfo) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <SkipLink />
        <div className="max-w-md w-full" data-testid="session-resume-ui">
          <div className="text-center mb-6">
            <PlayCircle className="h-12 w-12 text-[#1d4ed8] mx-auto mb-3" />
            <h1 className="text-2xl font-black mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Welcome back!
            </h1>
            <p className="text-sm text-[#475569]">
              You have an unfinished conversation for "{resumeInfo.form_title}".
            </p>
          </div>

          <div className="bg-[#F8FAFC] rounded-xl p-4 mb-4 border border-[#E2E8F0]">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-semibold">Progress</span>
              <span className="text-sm text-[#1d4ed8] font-bold">{resumeInfo.progress}%</span>
            </div>
            <div className="w-full bg-[#E2E8F0] rounded-full h-2 mb-4">
              <div className="bg-[#1d4ed8] h-2 rounded-full transition-all" style={{ width: `${resumeInfo.progress}%` }} />
            </div>

            <h3 className="text-xs font-semibold text-[#475569] mb-2">Already captured:</h3>
            <div className="space-y-1.5 max-h-40 overflow-y-auto">
              {resumeInfo.captured_summary?.map((item, i) => (
                <div key={`item-${i}`} className="flex items-start gap-2 text-xs">
                  <CheckCircle2 className="h-3.5 w-3.5 text-green-600 shrink-0 mt-0.5" />
                  <span><span className="font-medium">{item.question}:</span> {item.answer}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              onClick={handleResume}
              className="flex-1 rounded-full bg-[#1d4ed8] hover:bg-[#1e40af]"
              data-testid="resume-session-btn"
            >
              <PlayCircle className="h-4 w-4 mr-2" />
              Continue
            </Button>
            <Button
              variant="outline"
              onClick={handleStartFresh}
              className="rounded-full"
              data-testid="start-fresh-btn"
            >
              Start Over
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <SkipLink />
        <div className="text-center" role="status" aria-live="polite">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1d4ed8] mx-auto" aria-hidden="true"></div>
          <p className="mt-4 text-[#475569]">Loading form...</p>
        </div>
      </div>
    );
  }

  if (!qrInfo) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <SkipLink />
        <div className="text-center max-w-md">
          <h1 className="text-3xl font-black mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Form Not Found
          </h1>
          <p className="text-[#475569] mb-6">This QR code is invalid or has been removed.</p>
          <Link to="/">
            <Button className="rounded-full bg-[#1d4ed8] hover:bg-[#1e40af]">
              Go to Homepage
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (startError && !session) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <SkipLink />
        <div className="text-center max-w-md" data-testid="form-load-error">
          <h1 className="text-2xl font-black mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Couldn't load this form
          </h1>
          <p className="text-[#475569] mb-6">{startError}</p>
          <Button
            className="rounded-full text-white"
            style={{ backgroundColor: brandColor }}
            onClick={() => { setStartError(''); setShowConsentGate(true); }}
            data-testid="form-load-retry-btn"
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <SkipLink />
      
      {/* Header */}
      <header className="border-b border-[#E2E8F0] px-4 py-4" role="banner">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center space-x-3 mb-2">
            {brandLogo ? (
              <img
                src={brandLogo}
                alt={companyName || 'Logo'}
                className="w-10 h-10 rounded-full object-contain bg-white border border-[#E2E8F0]"
                onError={(e) => { e.target.style.display = 'none'; }}
                data-testid="brand-logo-header"
              />
            ) : (
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: brandColor }} aria-hidden="true">
                <MessageSquare className="h-5 w-5 text-white" />
              </div>
            )}
            <div>
              <h1 className="font-bold text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {qrInfo.title}
              </h1>
              {companyName && (
                <p className="text-xs font-semibold" style={{ color: brandColor }} data-testid="brand-name-header">
                  {companyName}
                </p>
              )}
              {qrInfo.description && (
                <p className="text-sm text-[#475569]">{qrInfo.description}</p>
              )}
              {interviewMode === 'adaptive' && (
                <span className="inline-flex items-center gap-1 text-xs bg-purple-50 text-purple-700 px-2 py-0.5 rounded-full mt-0.5" data-testid="adaptive-mode-indicator">
                  <Brain className="h-3 w-3" />
                  AI Interview
                </span>
              )}
            </div>
          </div>
          {session?.status === 'completed' && (
            <div className="mt-2" role="status" data-testid="completion-banner">
              {submissionResult && submissionResult.submitted === false ? (
                <div className="flex items-start gap-2 text-sm text-amber-700" data-testid="submission-failed">
                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" aria-hidden="true" />
                  <span>Οι απαντήσεις σας αποθηκεύτηκαν με ασφάλεια, αλλά δεν στάλθηκαν αυτόματα στη φόρμα. Ο οργανισμός θα τις λάβει.</span>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-sm text-green-600" data-testid="submission-success">
                  <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                  <span>Η φόρμα ολοκληρώθηκε και οι απαντήσεις στάλθηκαν</span>
                </div>
              )}
              <p className="text-sm text-[#334155] mt-2" data-testid="participation-confirmation">
                Η συμμετοχή σας καταγράφηκε. Σας ευχαριστούμε θερμά για τον χρόνο και την εμπιστοσύνη σας.
              </p>
              {session?.support_note && (
                <div className="mt-2 flex items-start gap-2 rounded-lg bg-[#F1F5F9] p-3 text-sm text-[#0f172a]" data-testid="support-note">
                  <Heart className="h-4 w-4 mt-0.5 shrink-0 text-rose-500" aria-hidden="true" />
                  <span>Αν χρειάζεστε υποστήριξη, μπορείτε να επικοινωνήσετε με την {session.support_note}.</span>
                </div>
              )}
            </div>
          )}
          {/* Progress indicator — Ερώτηση X από Y */}
          {session && session.status !== 'completed' && progress.total > 0 && (
            <div className="mt-3" data-testid="progress-indicator">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-semibold text-[#475569]">
                  Ερώτηση {Math.min(progress.step || (progress.answered + 1), progress.total)} από {progress.total}
                </span>
                <span className="text-xs text-[#94A3B8]" data-testid="progress-percent">
                  {Math.round((progress.answered / progress.total) * 100)}%
                </span>
              </div>
              <div className="h-1.5 w-full rounded-full bg-[#F1F5F9] overflow-hidden" role="progressbar" aria-valuenow={progress.answered} aria-valuemin={0} aria-valuemax={progress.total}>
                <motion.div
                  className="h-full rounded-full"
                  style={{ backgroundColor: brandColor }}
                  initial={{ width: 0 }}
                  animate={{ width: `${Math.min((progress.answered / progress.total) * 100, 100)}%` }}
                  transition={{ duration: 0.4 }}
                />
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Live region for screen readers */}
      <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
        {lastMessage && `AI assistant: ${lastMessage}`}
      </div>

      {/* Messages */}
      <main
        id="main-content"
        className="flex-1 overflow-y-auto px-4 py-8"
        role="log"
        aria-label="Conversation messages"
        aria-live="polite"
      >
        <div className="max-w-2xl mx-auto space-y-6">
          {welcomeText && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="flex justify-start"
              data-testid="branded-welcome-message"
            >
              <div className="chat-ai-bubble max-w-[80%] px-6 py-3">
                <p className="whitespace-pre-wrap">{welcomeText}</p>
              </div>
            </motion.div>
          )}
          <AnimatePresence>
            {messages.map((message, index) => (
              <motion.div
                key={message.id || `msg-${index}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                role="article"
                aria-label={`${message.role === 'user' ? 'You' : 'AI Assistant'} said`}
              >
                <div
                  className={`max-w-[80%] px-6 py-3 ${
                    message.role === 'user'
                      ? 'chat-user-bubble'
                      : 'chat-ai-bubble'
                  }`}
                >
                  <p className="whitespace-pre-wrap">{message.content}</p>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
          
          {sending && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex justify-start"
              role="status"
              aria-label="AI is typing"
            >
              <div className="chat-ai-bubble px-6 py-3">
                <div className="flex space-x-2" aria-hidden="true">
                  <div className="w-2 h-2 bg-[#475569] rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-2 h-2 bg-[#475569] rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-2 h-2 bg-[#475569] rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
                <span className="sr-only">AI is typing</span>
              </div>
            </motion.div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
      </main>

      {/* Input */}
      <div className="sticky bottom-0 bg-white border-t border-[#E2E8F0] px-4 py-4">
        <div className="max-w-2xl mx-auto">
          {/* Prominent voice status indicator */}
          <AnimatePresence>
            {voicePhase !== 'idle' && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 8 }}
                className="flex justify-center mb-3"
                data-testid="voice-phase-banner"
              >
                {voicePhase === 'listening' && (
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-50 border border-red-200 text-red-600" data-testid="voice-phase-listening">
                    <span className="relative flex h-3 w-3">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                    </span>
                    <Mic className="h-4 w-4" aria-hidden="true" />
                    <span className="text-sm font-semibold">Ακούω…</span>
                  </div>
                )}
                {voicePhase === 'speaking' && (
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700" data-testid="voice-phase-speaking">
                    <Volume2 className="h-4 w-4 animate-pulse" aria-hidden="true" />
                    <span className="text-sm font-semibold">Μιλάω…</span>
                  </div>
                )}
                {voicePhase === 'processing' && (
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#EFF6FF] border border-[#BFDBFE] text-[#1d4ed8]" data-testid="voice-phase-processing">
                    <span className="w-4 h-4 border-2 border-[#1d4ed8] border-t-transparent rounded-full animate-spin" aria-hidden="true"></span>
                    <span className="text-sm font-semibold">Επεξεργασία…</span>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
          {session?.status === 'paused' ? (
            <div className="text-center space-y-3" data-testid="session-paused-cta">
              <p className="text-[#475569] text-sm">Session paused. Your progress is saved!</p>
              <Button
                onClick={() => { setSession(prev => ({ ...prev, status: 'in_progress' })); inputRef.current?.focus(); }}
                className="rounded-full bg-[#1d4ed8] hover:bg-[#1e40af] px-6 text-sm"
                data-testid="session-resume-button"
              >
                Resume Conversation
              </Button>
            </div>
          ) : (
          <form onSubmit={handleSend} className="flex items-center space-x-3">
            <VoiceMode
              sessionId={session?.id}
              chatEndpoint={`${API}/public/sessions/${session?.id}/chat`}
              onNewMessages={() => fetchMessages()}
              disabled={sending || session?.status === 'completed'}
              voice={session?.voice_preference || qrInfo?.voice_preference || 'nova'}
              language={session?.form_language || 'el'}
              autoStart={autoStartVoice}
              autoStartSpeakText={latestAssistant}
              onPhaseChange={setVoicePhase}
              sharedAudioRef={sharedAudioRef}
            />
            <label htmlFor="message-input" className="sr-only">
              Type your response to the form question
            </label>
            <Input
              id="message-input"
              ref={inputRef}
              type="text"
              placeholder="Type or use voice..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              disabled={sending || session?.status === 'completed'}
              className="flex-1 rounded-full text-base focus:ring-4 focus:ring-[#1d4ed8] focus:ring-offset-2"
              autoFocus
            />
            <Button
              type="submit"
              disabled={!input.trim() || sending || session?.status === 'completed'}
              className="rounded-full text-white btn-hover-lift px-6"
              style={{ backgroundColor: brandColor }}
              aria-label={sending ? 'Sending message' : 'Send message'}
            >
              <Send className="h-4 w-4" aria-hidden="true" />
            </Button>
          </form>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="text-center py-4 text-xs text-[#475569] border-t border-[#E2E8F0]">
        Powered by <Link to="/" className="text-[#1d4ed8] hover:underline">Evenly Respond</Link>
      </footer>
    </div>
  );
};
